import type {ReactElement} from 'react';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {PopupMenu} from '@Components/popup-menu';
import {PopupMenuItem} from '@Components/popup-menu/components';
import {Text, TextSize} from '@Components/text';
import {formatDateFromTimestamp, formatLocalDate} from '@Utils/date.util';
import type {WizardScheduleControlsProps} from './wizard-schedule-controls.types';
import styles from './wizard-schedule-controls.module.scss';

export function WizardScheduleControls({
  socialPost,
  isLargeBreakpoint,
  isMediumBreakpoint,
  areCaptionsBeingAutoGenerated,
  onDeleteSchedule,
  onOpenDateTimePicker,
}: WizardScheduleControlsProps): ReactElement {
  const getDateString = (dateTimeString: string): string[] => {
    const parts = dateTimeString.split(', ');
    return [`${parts[0]}, ${parts[1]}`, parts[2]];
  };

  const getScheduleContainer = (): null | ReactElement => {
    if (!socialPost?.publishOn) {
      return null;
    }

    const stringDate = formatLocalDate(socialPost.publishOn);
    const dateTimeArray = getDateString(stringDate);
    const time = dateTimeArray[1];
    const date = formatDateFromTimestamp(socialPost.publishOn);

    return (
      <div className="flex-column-justify-between">
        <Text val={date} bold size={TextSize.XSMALL} className="content-body-white" />
        <Text val={time} bold size={TextSize.XXSMALL} className="color-extra-light" />
      </div>
    );
  };

  const getIconMenuMarkup = (): ReactElement => {
    if (isMediumBreakpoint) {
      return <Icon icon="icon icon-more-h" size={IconSize.SIZE_ICON_16} type={IconType.DARK_SECONDARY} shape={IconShape.SQUARE} isDisabled={areCaptionsBeingAutoGenerated} />;
    }
    return (
      <Icon
        id="publish-options-menu-icon"
        className="spacing-m-r-2"
        icon="icon-more-h"
        size={IconSize.SIZE_ICON_20}
        shape={IconShape.SQUARE}
        type={IconType.GHOST}
        isDisabled={areCaptionsBeingAutoGenerated}
      />
    );
  };

  const getEditIconsGroup = (): ReactElement => {
    return (
      <>
        <Icon
          onClick={onDeleteSchedule}
          icon="icon icon-delete"
          size={IconSize.SIZE_ICON_16}
          type={IconType.DARK_SECONDARY}
          shape={IconShape.SQUARE}
          className="spacing-m-r-2"
        />
        <Icon
          onClick={onOpenDateTimePicker}
          icon="icon icon-pencil"
          size={IconSize.SIZE_ICON_16}
          type={IconType.DARK_SECONDARY}
          shape={IconShape.SQUARE}
          flatIconType={{color: FlatIconColor.PRIMARY, type: FlatIconColorType.DARKER}}
        />
      </>
    );
  };

  const getEditScheduleMenu = (): ReactElement => {
    return (
      <PopupMenu menuToggleButton={getIconMenuMarkup()} maxWidth={undefined} scrollingContainerSelector={undefined}>
        <PopupMenuItem text={window.i18next.t('pmwjs_reschedule')} iconClasses="icon icon-pencil" clickHandler={onOpenDateTimePicker} />
        <PopupMenuItem text={window.i18next.t('pmwjs_remove')} iconClasses="icon icon-delete" clickHandler={onDeleteSchedule} />
      </PopupMenu>
    );
  };

  return (
    <div id="add-details-onboarding-step-three" className={`_full-width spacing-m-r-2 flex-align-items-center flex-row-justify-between ${styles.scheduleContainer}`}>
      <div className="flex-h-row flex-align-items-center spacing-m-r-2">{getScheduleContainer()}</div>
      <div className="flex-h-row">{isLargeBreakpoint ? getEditIconsGroup() : getEditScheduleMenu()}</div>
    </div>
  );
}
